import React from "react";
import { TrendingUp } from "lucide-react";
// Inline helper to avoid importing from outside `src/`.
const hexToRgba = (hex, alpha = 0.12) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getStatIconBackground = (color) => {
  if (!color) return 'rgba(0,0,0,0.06)';
  if (typeof color === 'string' && color.startsWith('#')) return hexToRgba(color, 0.12);
  return color;
};

const StatsGrid = ({ 
  styles = {}, 
  stats = [], 
  screenSize = {}, 
  defaultSymbolBgAlpha = 0.12,
  gridStyle = {} // Allow custom grid wrapper style
}) => {
  const renderContent = (content) => {
    if (React.isValidElement(content)) return content;
    if (typeof content === 'function') return React.createElement(content, {});
    // Preserve numeric 0 and boolean false; treat only null/undefined as empty
    if (content === null || content === undefined) return '';
    return String(content);
  };

  return (
    <div style={{ ...(styles.statsGrid || {}), ...gridStyle }}>
      {stats.map((stat, index) => {
        const Icon = stat.icon;

        // Per-card options with sensible defaults
        const showSymbol = stat.showSymbol !== undefined ? stat.showSymbol : true;
        const showSubtitle = stat.showSubtitle !== undefined ? stat.showSubtitle : !!stat.subtitle;
        const cardHeightStyle = stat.cardHeight ? { height: stat.cardHeight } : {};
        const cardWidthStyle = stat.cardWidth
          ? { width: typeof stat.cardWidth === 'number' ? `${stat.cardWidth}px` : stat.cardWidth, justifySelf: 'center' }
          : {};
        const rawValueAlign = stat.valueAlign ?? styles?.statValue?.textAlign ?? 'left';
        let valueAlignStyle = {};
        if (typeof rawValueAlign === 'number') {
          // use logical margin so it respects RTL/LTR (`marginInlineStart`)
          valueAlignStyle = { marginInlineStart: `${rawValueAlign}px` };
        } else {
          valueAlignStyle = { textAlign: rawValueAlign };
        }
        const valueCustomStyle = stat.valueStyle || {};

        const iconBg = (stat.iconBackground === null)
          ? 'transparent'
          : getStatIconBackground(stat.iconBackground || stat.color || undefined, defaultSymbolBgAlpha);

        const headerGap = stat.titleGap !== undefined ? stat.titleGap : styles?.statHeader?.gap;
        const headerStyle = headerGap !== undefined ? { ...styles.statHeader, gap: headerGap } : styles.statHeader;
        const titleStyle = { ...(styles.statTitle || {}), ...(stat.titleStyle || {}) };
        const subtitleStyle = { ...(styles.statSubtitle || {}), ...(stat.subtitleStyle || {}) };

        // Allow full card style override per stat
        const cardStyle = stat.cardStyle ? { ...(styles.statCard || {}), ...stat.cardStyle } : styles.statCard;

        return (
          <div
            key={index}
            style={{ ...cardStyle, ...cardHeightStyle, ...cardWidthStyle }}
          >
            <div style={headerStyle}>
              {showSymbol && (
                <div
                  style={{
                    ...styles.statIconWrapper,
                    background: iconBg,
                    ...(stat.iconWrapperStyle || {}),
                  }}
                >
                  {Icon ? <Icon size={screenSize.isMobile ? 20 : 24} /> : null}
                </div>
              )}
              <p style={titleStyle}>{renderContent(stat.title)}</p>
            </div>
            <h2 style={{ ...styles.statValue, ...valueAlignStyle, ...valueCustomStyle }}>
              {renderContent(stat.value)}
            </h2>
            {showSubtitle && stat.subtitle ? (
              <p style={subtitleStyle}>
                {renderContent(stat.subtitle)}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default StatsGrid;